import React from 'react';
import {
    Typography,
    Link,
    Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PlaceIcon from '@mui/icons-material/Place';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import { API_BASE_URL } from 'config/api';
import {
    PartnersContainer,
    PartnersHeader,
    PartnersGrid,
    VenueCard,
    VenueImageContainer,
    VenueImage,
    VenueContent,
    VenueName,
    VenueAddress,
    CommunityBadge,
    MemberDiscountBadge,
    VenueFooter,
    PriceDisplay,
    BookButton,
} from './styled';

const PartnersView = ({ venues, communitySlug, onVenueClick }) => {
    const navigate = useNavigate();
    
    if (!venues || venues.length === 0) {
        return null;
    }

    const handleVenueClick = (venue) => {
        onVenueClick?.(venue);
    };

    const handleBookClick = (e, venue) => {
        e.stopPropagation();
        // Navigate to booking page with venue pre-selected
        navigate(`/booking-find?venue=${venue.venue?.slug || ''}`);
    };

    const getGoogleMapsLink = (venue) => {
        if (venue.venue?.google_maps_url) {
            return venue.venue.google_maps_url;
        }
        if (venue.venue?.single_address) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.venue.single_address)}`;
        }
        return null;
    };

    const getImageUrl = (image) => {
        if (!image) return null;
        return image.startsWith('/') ? `${API_BASE_URL}${image}` : image;
    };

    return (
        <PartnersContainer>
            <PartnersHeader>
                <Typography variant="h6" fontWeight={600}>
                    Partners
                </Typography>
                <Button
                    variant="text"
                    endIcon={<ArrowForwardIcon />}
                    size="small"
                    sx={{ 
                        textTransform: 'none',
                        color: 'primary.main',
                        fontWeight: 500,
                    }}
                >
                    View All
                </Button>
            </PartnersHeader>

            <PartnersGrid>
                {venues.slice(0, 6).map((partnerVenue, index) => {
                    const venue = partnerVenue.venue;
                    const community = partnerVenue.group;
                    const googleMapsLink = getGoogleMapsLink(partnerVenue);
                    const hasDiscount = partnerVenue.discount_percentage && partnerVenue.discount_percentage > 0;
                    const basePrice = venue?.min_price || 15; // fallback price
                    const discountedPrice = hasDiscount 
                        ? (basePrice * (1 - partnerVenue.discount_percentage / 100)).toFixed(0)
                        : basePrice;
                    
                    return (
                        <VenueCard 
                            key={venue?.slug || index}
                            onClick={() => handleVenueClick(partnerVenue)}
                        >
                            <VenueImageContainer>
                                {community?.name && (
                                    <CommunityBadge>
                                        {community.name}
                                    </CommunityBadge>
                                )}
                                {hasDiscount && (
                                    <MemberDiscountBadge>
                                        <LocalOfferIcon sx={{ fontSize: '0.7rem' }} />
                                        {partnerVenue.discount_percentage}% OFF
                                    </MemberDiscountBadge>
                                )}
                                <VenueImage
                                    src={getImageUrl(venue?.image)}
                                    alt={venue?.name || 'Venue'}
                                    onError={(e) => {
                                        // Create a simple gray placeholder as base64 to avoid infinite loop
                                        e.target.onError = null; // Remove event handler to prevent infinite loop
                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjE4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                                    }}
                                />
                            </VenueImageContainer>

                            <VenueContent>
                                <div>
                                    <VenueName>
                                        {venue?.name || 'Venue Name'}
                                    </VenueName>

                                    {venue?.single_address && googleMapsLink && (
                                        <Link
                                            href={googleMapsLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                textDecoration: 'none',
                                                '&:hover': { color: 'primary.main' }
                                            }}
                                        >
                                            <VenueAddress>
                                                <PlaceIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
                                                <Typography 
                                                    variant="body2" 
                                                    noWrap 
                                                    sx={{ fontSize: '0.875rem' }}
                                                >
                                                    {venue.single_address}
                                                </Typography>
                                            </VenueAddress>
                                        </Link>
                                    )}
                                </div>

                                <VenueFooter>
                                    <PriceDisplay>
                                        <span className="price-label">From</span>
                                        <span className="price-value">€{discountedPrice}/day</span>
                                        {hasDiscount && (
                                            <span className="price-original">€{basePrice}</span>
                                        )}
                                    </PriceDisplay>
                                    <BookButton onClick={(e) => handleBookClick(e, partnerVenue)}>
                                        <EventSeatIcon sx={{ fontSize: '1rem' }} />
                                        Book
                                    </BookButton>
                                </VenueFooter>
                            </VenueContent>
                        </VenueCard>
                    );
                })}
            </PartnersGrid>
        </PartnersContainer>
    );
};

export default PartnersView;